import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Combines class names using clsx and tailwind-merge for optimal Tailwind CSS class merging.
 * This utility handles conditional classes, string arrays, and objects while automatically
 * resolving Tailwind CSS conflicts by merging similar utility classes.
 *
 * @param inputs - Class values to be combined. Can be strings, objects, arrays, or falsy values
 * @returns A single string of merged CSS classes
 *
 * @example
 * cn("px-4 py-2", true && "bg-blue-500", { "text-white": isActive })
 * // Returns: "px-4 py-2 bg-blue-500 text-white"
 *
 * @example
 * cn("px-4", "px-8") // tailwind-merge will resolve conflicts
 * // Returns: "px-8" (last conflicting class wins)
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Formats a date string into a human-readable format
 * @param date - Date string or Date object to format
 * @returns Formatted date string (e.g., "January 1, 2024")
 */
export function formatDate(date: string | Date): string {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Truncates a string to a specified length and adds ellipsis
 * @param str - String to truncate
 * @param length - Maximum length (default: 100)
 * @returns Truncated string with ellipsis if needed
 */
export function truncateString(string_: string, length: number = 100): string {
  if (string_.length <= length) return string_;
  return string_.slice(0, length).trim() + '...';
}

/**
 * Debounces a function call to improve performance
 * @param func - Function to debounce
 * @param delay - Delay in milliseconds (default: 300)
 * @returns Debounced function
 */
export function debounce<T extends (...arguments_: any[]) => any>(
  function_: T,
  delay: number = 300
): (...arguments_: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout;
  return (...arguments_: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => function_(...arguments_), delay);
  };
}
